/**
 * useFileBrowserSites Hook
 *
 * React Query hook for fetching SharePoint sites for the file browser.
 * Uses React Query caching with staleTime derived from backend cache settings.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantApiService } from '../services/tenantApi';
import { useCacheSettings } from './useCacheSettings';
import type { SharePointSite } from '../types/sharepoint';

/**
 * Query key factory for file browser sites
 */
export const fileBrowserSitesKeys = {
  all: ['fileBrowserSites'] as const,
  list: (tenantId: string) => ['fileBrowserSites', tenantId] as const,
};

/**
 * Default stale time in milliseconds (5 minutes)
 * Used when cache settings aren't available
 */
const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Hook return type
 */
export interface UseFileBrowserSitesResult {
  sites: SharePointSite[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook options
 */
interface UseFileBrowserSitesOptions {
  tenantId: string;
  enabled?: boolean;
}

/**
 * React Query hook for fetching SharePoint sites.
 *
 * Features:
 * - Automatic caching with React Query
 * - Stale time derived from backend cache settings (sites TTL)
 * - Manual refresh bypasses both frontend and backend caches
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.enabled - Whether to fetch (default: true)
 * @returns Sites data, loading states, and refetch function
 */
export function useFileBrowserSites({
  tenantId,
  enabled = true,
}: UseFileBrowserSitesOptions): UseFileBrowserSitesResult {
  const queryClient = useQueryClient();

  // Get cache settings to derive staleTime
  const { settings: cacheSettings } = useCacheSettings();

  // Calculate staleTime from backend TTL (convert seconds to ms)
  // Use slightly shorter time than backend to ensure smooth refresh
  const staleTimeMs = cacheSettings?.ttlSeconds?.sites
    ? (cacheSettings.ttlSeconds.sites - 60) * 1000 // 1 minute shorter than backend
    : DEFAULT_STALE_TIME_MS;

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: fileBrowserSitesKeys.list(tenantId),
    queryFn: async () => {
      return TenantApiService.getMySites(tenantId, false);
    },
    enabled: enabled && !!tenantId,
    staleTime: staleTimeMs,
  });

  // Manual refetch bypasses backend cache
  const refetch = async () => {
    // Invalidate the query to force refetch
    await queryClient.invalidateQueries({
      queryKey: fileBrowserSitesKeys.list(tenantId),
    });
    // Fetch with refresh=true to bypass backend cache
    const freshData = await TenantApiService.getMySites(tenantId, true);
    // Update the cache with fresh data
    queryClient.setQueryData(fileBrowserSitesKeys.list(tenantId), freshData);
  };

  // Sort sites alphabetically by displayName for consistent ordering
  const sortedSites = (data ?? []).slice().sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  return {
    sites: sortedSites,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
